/**
 * Dynamic Sitemap Generator
 * 
 * This utility generates sitemap XML content from the project's data files.
 * It automatically includes all blog posts, services, and location pages.
 * 
 * To regenerate the sitemap:
 * 1. Run the generateSitemapXml() function
 * 2. Copy the output to public/sitemap.xml
 * 
 * Or use the downloadSitemap() function to download directly from the browser.
 */

import { blogPosts } from '@/data/blogData';
import { townData } from '@/data/townData';
import { enhancedServiceData } from '@/data/enhancedServiceData';

const SITE_URL = 'https://pklandscapect.com';
const LAST_MOD = new Date().toISOString().split('T')[0]; // Today's date

interface SitemapUrl {
  loc: string;
  lastmod: string;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority: string;
}

// Static pages with their configuration
const staticPages: SitemapUrl[] = [
  { loc: '/', lastmod: LAST_MOD, changefreq: 'weekly', priority: '1.0' },
  { loc: '/about', lastmod: LAST_MOD, changefreq: 'monthly', priority: '0.8' },
  { loc: '/services', lastmod: LAST_MOD, changefreq: 'weekly', priority: '0.9' },
  { loc: '/portfolio', lastmod: LAST_MOD, changefreq: 'weekly', priority: '0.8' },
  { loc: '/testimonials', lastmod: LAST_MOD, changefreq: 'monthly', priority: '0.7' },
  { loc: '/contact', lastmod: LAST_MOD, changefreq: 'monthly', priority: '0.8' },
  { loc: '/commercial', lastmod: LAST_MOD, changefreq: 'monthly', priority: '0.8' },
  { loc: '/blog', lastmod: LAST_MOD, changefreq: 'weekly', priority: '0.8' },
  { loc: '/locations', lastmod: LAST_MOD, changefreq: 'monthly', priority: '0.7' },
];

/**
 * Get all service URLs from enhanced service data
 */
export function getServiceUrls(): SitemapUrl[] {
  return Object.values(enhancedServiceData).map((service) => ({
    loc: `/services/${service.slug}`,
    lastmod: LAST_MOD,
    changefreq: 'monthly' as const,
    priority: '0.8',
  }));
}

/**
 * Get all blog post URLs
 */
export function getBlogUrls(): SitemapUrl[] {
  return blogPosts.map((post) => ({
    loc: `/blog/${post.slug}`,
    lastmod: post.date || LAST_MOD,
    changefreq: 'monthly' as const,
    priority: '0.7',
  }));
}

/**
 * Get all location/town URLs
 */
export function getLocationUrls(): SitemapUrl[] {
  return townData.map((town) => ({
    loc: `/locations/${town.slug}`,
    lastmod: LAST_MOD,
    changefreq: 'monthly' as const,
    priority: town.priority === 'high' ? '0.8' : town.priority === 'medium' ? '0.7' : '0.6',
  }));
}

/**
 * Get all URLs for the sitemap
 */
export function getAllUrls(): SitemapUrl[] {
  return [
    ...staticPages,
    ...getServiceUrls(),
    ...getLocationUrls(),
    ...getBlogUrls(),
  ];
}

/**
 * Generate the sitemap XML string
 */
export function generateSitemapXml(): string {
  const urls = getAllUrls();
  
  const urlEntries = urls.map((url) => `  <url>
    <loc>${SITE_URL}${url.loc}</loc>
    <lastmod>${url.lastmod}</lastmod>
    <changefreq>${url.changefreq}</changefreq>
    <priority>${url.priority}</priority>
  </url>`).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`;
}

/**
 * Get sitemap statistics
 */
export function getSitemapStats(): {
  totalUrls: number;
  staticPages: number;
  services: number;
  locations: number;
  blogPosts: number;
} {
  return {
    totalUrls: getAllUrls().length,
    staticPages: staticPages.length,
    services: getServiceUrls().length,
    locations: getLocationUrls().length,
    blogPosts: getBlogUrls().length,
  };
}

/**
 * Download sitemap as XML file (for use in browser)
 */
export function downloadSitemap(): void {
  const xml = generateSitemapXml();
  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sitemap.xml';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copy sitemap XML to clipboard
 */
export async function copySitemapToClipboard(): Promise<boolean> {
  try {
    const xml = generateSitemapXml();
    await navigator.clipboard.writeText(xml);
    return true;
  } catch (error) {
    console.error('Failed to copy sitemap:', error);
    return false;
  }
}
